/**
 * UI primitives. Deliberately small and explicit: the console is an operations tool,
 * so the vocabulary is panels, meters, badges and dense tables — not a component zoo.
 *
 * Accessibility is part of the primitive's contract: every input has a visible label,
 * every interactive element is reachable by keyboard, and focus order follows reading
 * order.
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { ApiRequestError } from '@/api/client'

export type Tone = 'neutral' | 'forge' | 'ok' | 'warn' | 'danger' | 'info' | 'ai'

const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'border-ink-600 bg-ink-800 text-mist-300',
  forge: 'border-forge-600/70 bg-forge-500/15 text-forge-300',
  ok: 'border-signal-ok/40 bg-signal-ok/10 text-signal-ok',
  warn: 'border-signal-warn/40 bg-signal-warn/10 text-signal-warn',
  danger: 'border-signal-danger/45 bg-signal-danger/10 text-signal-danger',
  info: 'border-signal-info/40 bg-signal-info/10 text-signal-info',
  ai: 'border-signal-ai/45 bg-signal-ai/10 text-signal-ai',
}

export function Badge({
  tone = 'neutral',
  children,
  title,
  testId,
}: {
  tone?: Tone
  children: ReactNode
  title?: string
  testId?: string
}) {
  return (
    <span
      data-testid={testId}
      title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[11px] leading-4 font-medium whitespace-nowrap ${TONE_CLASSES[tone]}`}
    >
      {children}
    </span>
  )
}

export function Cap({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`label-cap ${className}`}>{children}</span>
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-forge-500 text-ink-950 hover:bg-forge-400 border border-forge-500 hover:border-forge-400 font-semibold',
  secondary: 'bg-ink-800 text-mist-100 border border-ink-600 hover:bg-ink-700 hover:border-ink-500',
  ghost: 'bg-transparent text-mist-300 border border-transparent hover:bg-ink-800 hover:text-mist-100',
  danger:
    'bg-signal-danger/15 text-signal-danger border border-signal-danger/50 hover:bg-signal-danger/25',
}

export function Button({
  variant = 'secondary',
  size = 'md',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' | 'md' }) {
  const sizing = size === 'sm' ? 'px-2.5 py-1 text-[12px]' : 'px-3.5 py-1.5 text-sm'
  return (
    <button
      type="button"
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${sizing} ${BUTTON_VARIANTS[variant]} ${className}`}
    />
  )
}

export function Panel({
  title,
  subtitle,
  actions,
  children,
  className = '',
  testId,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  testId?: string
}) {
  return (
    <section data-testid={testId} className={`panel ${className}`}>
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b border-ink-750 px-4 py-3">
          <div className="min-w-0">
            {typeof title === 'string' ? (
              <h2 className="text-[13px] font-semibold tracking-wide text-mist-100">{title}</h2>
            ) : (
              title
            )}
            {subtitle ? <p className="mt-0.5 text-[12px] text-mist-400">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </header>
      )}
      <div className="px-4 py-3.5">{children}</div>
    </section>
  )
}

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string
  hint?: ReactNode
  error?: string | null
  htmlFor: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="label-cap">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-[12px] text-signal-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12px] text-mist-500">{hint}</p>
      ) : null}
    </div>
  )
}

const INPUT_BASE =
  'w-full rounded-md border border-ink-600 bg-ink-850 px-2.5 py-1.5 text-sm text-mist-100 placeholder:text-mist-500 focus:border-forge-400 focus:outline-none'

export function Input({ className = '', ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={`${INPUT_BASE} ${className}`} />
}

export function Textarea({ className = '', ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={`${INPUT_BASE} min-h-[64px] ${className}`} />
}

export function Select({ className = '', ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...rest} className={`${INPUT_BASE} pr-8 ${className}`} />
}

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  testId,
  hint,
}: {
  label: ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  testId?: string
  hint?: ReactNode
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 transition-colors ${
        checked ? 'border-forge-600/60 bg-forge-500/10' : 'border-ink-700 bg-ink-850 hover:border-ink-600'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-[3px] size-4 shrink-0 accent-forge-400"
      />
      <span className="min-w-0">
        <span className="block text-[13px] leading-5 text-mist-200">{label}</span>
        {hint ? <span className="mt-0.5 block text-[12px] text-mist-500">{hint}</span> : null}
      </span>
    </label>
  )
}

export function Meter({
  value,
  max,
  tone = 'forge',
  label,
  testId,
}: {
  value: number
  max: number
  tone?: Tone
  label?: string
  testId?: string
}) {
  const ratio = max <= 0 ? 0 : Math.min(1, Math.max(0, value / max))
  const barTone =
    tone === 'danger'
      ? 'bg-signal-danger'
      : tone === 'warn'
        ? 'bg-signal-warn'
        : tone === 'ok'
          ? 'bg-signal-ok'
          : 'bg-forge-400'
  return (
    <div
      data-testid={testId}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
      className="h-1.5 w-full overflow-hidden rounded-full bg-ink-750"
    >
      <div className={`h-full rounded-full ${barTone}`} style={{ width: `${ratio * 100}%` }} />
    </div>
  )
}

export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  testId,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: Tone
  testId?: string
}) {
  const valueTone =
    tone === 'ok'
      ? 'text-signal-ok'
      : tone === 'danger'
        ? 'text-signal-danger'
        : tone === 'warn'
          ? 'text-signal-warn'
          : tone === 'forge'
            ? 'text-forge-300'
            : 'text-mist-100'
  return (
    <div data-testid={testId} className="rounded-lg border border-ink-750 bg-ink-850 px-3 py-2.5">
      <Cap>{label}</Cap>
      <div className={`num mt-1 text-[19px] leading-7 font-semibold ${valueTone}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-[12px] text-mist-500">{hint}</div> : null}
    </div>
  )
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span role="status" aria-label={label} className="inline-flex items-center gap-2 text-mist-400">
      <span className="size-3 animate-spin rounded-full border-[1.5px] border-ink-600 border-t-forge-400" />
      <span className="text-[12px]">{label}</span>
    </span>
  )
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed border-ink-700 bg-ink-900/60 px-4 py-6">
      <p className="text-sm font-medium text-mist-200">{title}</p>
      {body ? <p className="max-w-prose text-[13px] text-mist-400">{body}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

/** One error surface for the whole console; renders the frozen ErrorResponse shape. */
export function ErrorNotice({ error, testId }: { error: unknown; testId?: string }) {
  if (!error) return null
  const apiError = error instanceof ApiRequestError ? error : null
  return (
    <div
      data-testid={testId ?? 'error-notice'}
      role="alert"
      className="rounded-lg border border-signal-danger/45 bg-signal-danger/10 px-3.5 py-3"
    >
      <div className="flex items-center gap-2">
        <span className="num text-[11px] font-semibold tracking-wide text-signal-danger">
          {apiError ? apiError.code : 'REQUEST_FAILED'}
        </span>
        {apiError ? <span className="num text-[11px] text-mist-500">HTTP {apiError.status}</span> : null}
      </div>
      <p className="mt-1 text-[13px] text-mist-200">
        {error instanceof Error ? error.message : String(error)}
      </p>
      {apiError?.fieldPath ? (
        <p className="num mt-1 text-[12px] text-mist-400">field: {apiError.fieldPath}</p>
      ) : null}
    </div>
  )
}

export function KeyValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Cap>{label}</Cap>
      <div className="text-[13px] text-mist-200">{children}</div>
    </div>
  )
}

export function MonoTag({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <code title={title} className="num rounded bg-ink-800 px-1.5 py-[1px] text-[11.5px] text-mist-300">
      {children}
    </code>
  )
}
